import { ChevronDown, LoaderCircle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { ModelAsset } from "../models/model-assets";
import type {
  CargoEditorBatchSelection,
  CargoEditorBatchSettingRegion,
  CargoEditorCargoAsset,
  CargoEditorDeviceBatchSelection,
  CargoEditorPalletReciprocatingLiftBatchSelection,
  CargoEditorPalletStackerCraneBatchSelection,
  CargoEditorRackBatchSelection,
  CargoEditorSingleStationReciprocatingShuttleBatchSelection
} from "../scenes/cargo-editor-scene";
import {
  createRackLayerRangeRows,
  ensureTrailingRackLayerRangeRow,
  parseRackLayerRangeRows,
  type RackLayerRangeRow
} from "./rack-layer-range";

type CargoRegionSettingsDialogProps = {
  busy: boolean;
  error?: string;
  onClose: () => void;
  onDeviceModelChange: (
    selection: CargoEditorDeviceBatchSelection,
    modelId: string
  ) => Promise<void>;
  onLiftLayerCountChange: (
    selection: CargoEditorPalletReciprocatingLiftBatchSelection,
    layerCount: number
  ) => void;
  onRackCargoChange: (
    selection: CargoEditorRackBatchSelection,
    asset?: CargoEditorCargoAsset
  ) => Promise<void>;
  onRackLayerHeightsChange: (
    selection: CargoEditorRackBatchSelection,
    layerHeights: number[]
  ) => Promise<void>;
  onShuttleHeightChange: (
    selection: CargoEditorSingleStationReciprocatingShuttleBatchSelection,
    height: number
  ) => void;
  onStackerCraneHeightChange: (
    selection: CargoEditorPalletStackerCraneBatchSelection,
    height: number
  ) => void;
  regions: CargoEditorBatchSettingRegion[];
};

type RackCargoDraft = "" | "clear" | CargoEditorCargoAsset;

type RackRegionDraft = {
  cargo: RackCargoDraft;
  kind: "rack-batch";
  layerRows: RackLayerRangeRow[];
  layerRowsChanged: boolean;
  selection: CargoEditorRackBatchSelection;
};

type DeviceRegionDraft = {
  kind: "device-batch";
  modelChanged: boolean;
  modelId: string;
  selection: CargoEditorDeviceBatchSelection;
};

type LiftRegionDraft = {
  kind: "pallet-reciprocating-lift-batch";
  layerCount: string;
  layerCountChanged: boolean;
  selection: CargoEditorPalletReciprocatingLiftBatchSelection;
};

type ShuttleRegionDraft = {
  height: string;
  heightChanged: boolean;
  kind: "single-station-reciprocating-shuttle-batch";
  selection: CargoEditorSingleStationReciprocatingShuttleBatchSelection;
};
type StackerCraneRegionDraft = {
  height: string;
  heightChanged: boolean;
  kind: "pallet-stacker-crane-batch";
  selection: CargoEditorPalletStackerCraneBatchSelection;
};

type RegionDraft =
  | DeviceRegionDraft
  | LiftRegionDraft
  | RackRegionDraft
  | ShuttleRegionDraft
  | StackerCraneRegionDraft;

export function CargoRegionSettingsDialog({
  busy,
  error,
  onClose,
  onDeviceModelChange,
  onLiftLayerCountChange,
  onRackCargoChange,
  onRackLayerHeightsChange,
  onShuttleHeightChange,
  onStackerCraneHeightChange,
  regions
}: CargoRegionSettingsDialogProps) {
  const [activeRegionId, setActiveRegionId] = useState(regions[0]?.id ?? "");
  const [drafts, setDrafts] = useState(() => createRegionDrafts(regions));
  const [localError, setLocalError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const activeRegion = regions.find(({ id }) => id === activeRegionId) ?? regions[0];
  const disabled = busy || submitting;

  useEffect(() => {
    if (!regions.some(({ id }) => id === activeRegionId)) {
      setActiveRegionId(regions[0]?.id ?? "");
    }
  }, [activeRegionId, regions]);

  const updateDraft = (key: string, update: (draft: RegionDraft) => RegionDraft) => {
    setDrafts((current) => {
      const draft = current[key];
      return draft ? { ...current, [key]: update(draft) } : current;
    });
  };

  const applyAndClose = async () => {
    if (disabled) {
      return;
    }
    const validationError = validateRegionDrafts(drafts);
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLocalError(undefined);
    setSubmitting(true);
    try {
      for (const draft of Object.values(drafts)) {
        if (draft.kind === "rack-batch") {
          if (draft.layerRowsChanged) {
            const parsed = parseRackLayerRangeRows(draft.layerRows);
            if (parsed.ok) {
              await onRackLayerHeightsChange(draft.selection, parsed.layerHeights);
            }
          }
          if (draft.cargo) {
            await onRackCargoChange(
              draft.selection,
              draft.cargo === "clear" ? undefined : draft.cargo
            );
          }
        } else if (draft.kind === "device-batch" && draft.modelChanged) {
          await onDeviceModelChange(draft.selection, draft.modelId);
        } else if (draft.kind === "pallet-reciprocating-lift-batch" && draft.layerCountChanged) {
          onLiftLayerCountChange(draft.selection, Number(draft.layerCount));
        } else if (draft.kind === "pallet-stacker-crane-batch" && draft.heightChanged) {
          onStackerCraneHeightChange(draft.selection, Number(draft.height) / 1000);
        } else if (
          draft.kind === "single-station-reciprocating-shuttle-batch" &&
          draft.heightChanged
        ) {
          onShuttleHeightChange(draft.selection, Number(draft.height) / 1000);
        }
      }
      onClose();
    } catch (applyError: unknown) {
      setLocalError(applyError instanceof Error ? applyError.message : "区域设置应用失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cargo-region-dialog-backdrop" role="presentation">
      <section
        aria-labelledby="cargo-region-dialog-title"
        aria-modal="true"
        className="cargo-region-dialog"
        role="dialog"
      >
        <header className="cargo-region-dialog-header">
          <div>
            <span>区域批量配置 · 关闭时统一应用</span>
            <h2 id="cargo-region-dialog-title">区域设定</h2>
          </div>
          <button
            aria-label="应用并关闭区域设定"
            disabled={disabled}
            onClick={() => void applyAndClose()}
            title="应用全部更改并关闭"
            type="button"
          >
            {submitting ? (
              <LoaderCircle aria-hidden="true" className="dxf-reference-spinner" size={18} />
            ) : (
              <X aria-hidden="true" size={18} />
            )}
          </button>
        </header>

        {regions.length === 0 ? (
          <div className="cargo-region-dialog-empty">
            <strong>当前图纸没有可设置的区域</strong>
            <p>
              请在 DXF
              中创建名称以“批量设定区域”开头的顶层图块，并将需要归组的货架、设备、提升机或穿梭车放入该图块。
            </p>
          </div>
        ) : (
          <div className="cargo-region-dialog-body">
            <nav aria-label="批量设定区域" className="cargo-region-list">
              {regions.map((region) => (
                <button
                  aria-current={region.id === activeRegion?.id ? "true" : undefined}
                  disabled={disabled}
                  key={region.id}
                  onClick={() => setActiveRegionId(region.id)}
                  type="button"
                >
                  <span>{region.name}</span>
                  <small>{countRegionMembers(region)} 个对象</small>
                </button>
              ))}
            </nav>

            <div className="cargo-region-settings">
              <div className="cargo-region-settings-heading">
                <div>
                  <span>当前区域</span>
                  <h3>{activeRegion?.name}</h3>
                </div>
                <small>{activeRegion ? countRegionMembers(activeRegion) : 0} 个可编辑对象</small>
              </div>

              {activeRegion?.selections.length ? (
                activeRegion.selections.map((selection) => {
                  const key = createRegionDraftKey(activeRegion.id, selection);
                  const draft = drafts[key];
                  return draft?.kind === "rack-batch" ? (
                    <RegionRackSettings
                      disabled={disabled}
                      draft={draft}
                      key={key}
                      onChange={(nextDraft) => updateDraft(key, () => nextDraft)}
                    />
                  ) : draft?.kind === "device-batch" ? (
                    <RegionDeviceSettings
                      disabled={disabled}
                      draft={draft}
                      key={key}
                      onChange={(nextDraft) => updateDraft(key, () => nextDraft)}
                    />
                  ) : draft?.kind === "pallet-reciprocating-lift-batch" ? (
                    <RegionLiftSettings
                      disabled={disabled}
                      draft={draft}
                      key={key}
                      onChange={(nextDraft) => updateDraft(key, () => nextDraft)}
                    />
                  ) : draft?.kind === "pallet-stacker-crane-batch" ? (
                    <RegionStackerCraneSettings
                      disabled={disabled}
                      draft={draft}
                      key={key}
                      onChange={(nextDraft) => updateDraft(key, () => nextDraft)}
                    />
                  ) : draft?.kind === "single-station-reciprocating-shuttle-batch" ? (
                    <RegionShuttleSettings
                      disabled={disabled}
                      draft={draft}
                      key={key}
                      onChange={(nextDraft) => updateDraft(key, () => nextDraft)}
                    />
                  ) : null;
                })
              ) : (
                <div className="cargo-region-settings-empty">
                  区域内没有中心点落入边界的可批量编辑对象。
                </div>
              )}
              {localError || error ? (
                <p className="cargo-region-dialog-error" role="alert">
                  {localError ?? error}
                </p>
              ) : null}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function RegionRackSettings({
  disabled,
  draft,
  onChange
}: {
  disabled: boolean;
  draft: RackRegionDraft;
  onChange: (draft: RackRegionDraft) => void;
}) {
  const selection = draft.selection;
  return (
    <section className="cargo-region-object-section">
      <div className="cargo-region-object-header">
        <div>
          <h4>{selection.rackType === "bin" ? "料箱货架" : "横梁式货架"}</h4>
          <span>{selection.rackIds.length} 组</span>
        </div>
        {selection.layerHeightsMixed ? <small>原参数不一致，已显示该类型默认值</small> : null}
      </div>

      <div className="cargo-region-layer-heading">
        <span>层数 / 连续范围</span>
        <span>净层高</span>
      </div>
      <div className="cargo-region-layer-rows">
        {draft.layerRows.map((row, index) => (
          <RegionLayerRow
            disabled={disabled}
            // biome-ignore lint/suspicious/noArrayIndexKey: The controlled rows are ordered layer ranges and have no independent identity.
            key={`${index}-${index === draft.layerRows.length - 1 ? "blank" : "value"}`}
            onChange={(nextRow) => {
              const nextRows = draft.layerRows.map((current, rowIndex) =>
                rowIndex === index ? nextRow : current
              );
              onChange({
                ...draft,
                layerRows: ensureTrailingRackLayerRangeRow(nextRows),
                layerRowsChanged: true
              });
            }}
            row={row}
          />
        ))}
      </div>
      <p className="cargo-region-field-note">
        可输入单层“3”或连续范围“3-5”，范围必须从第 1 层连续填写；最后一层编号就是总层数。层高单位为
        mm，表示不含横杆的净高度。更改将在关闭弹窗时统一应用。
      </p>

      <label className="cargo-region-select-field">
        <span>全部设置货物</span>
        <div>
          <select
            disabled={disabled}
            onChange={(event) =>
              onChange({ ...draft, cargo: event.target.value as RackCargoDraft })
            }
            value={draft.cargo}
          >
            <option value="">不修改货物</option>
            <option value="clear">清空所有货物</option>
            {selection.rackType === "bin" ? (
              <option value={ModelAsset.BinCargo}>全部设置为料箱</option>
            ) : (
              <>
                <option value={ModelAsset.WoodenCrate}>全部设置为木板货物</option>
                <option value={ModelAsset.SteelCoil}>全部设置为钢卷</option>
              </>
            )}
          </select>
          <ChevronDown aria-hidden="true" size={15} />
        </div>
      </label>
    </section>
  );
}

function RegionLayerRow({
  disabled,
  onChange,
  row
}: {
  disabled: boolean;
  onChange: (row: RackLayerRangeRow) => void;
  row: RackLayerRangeRow;
}) {
  return (
    <div className="cargo-region-layer-row">
      <input
        aria-label="层数或连续范围"
        disabled={disabled}
        onChange={(event) => onChange({ ...row, range: event.target.value })}
        placeholder="例如 1-3"
        value={row.range}
      />
      <label>
        <input
          aria-label="净层高"
          disabled={disabled}
          min="1"
          onChange={(event) => onChange({ ...row, height: event.target.value })}
          placeholder="例如 1300"
          step="1"
          type="number"
          value={row.height}
        />
        <span>mm</span>
      </label>
    </div>
  );
}

function RegionDeviceSettings({
  disabled,
  draft,
  onChange
}: {
  disabled: boolean;
  draft: DeviceRegionDraft;
  onChange: (draft: DeviceRegionDraft) => void;
}) {
  const selection = draft.selection;
  return (
    <section className="cargo-region-object-section">
      <div className="cargo-region-object-header">
        <div>
          <h4>设备 · {selection.blockPrefix}</h4>
          <span>{selection.deviceIds.length} 台</span>
        </div>
        {selection.modelMixed ? <small>原型号不一致，请选择统一型号</small> : null}
      </div>
      <label className="cargo-region-select-field">
        <span>统一设备型号</span>
        <div>
          <select
            disabled={disabled}
            onChange={(event) =>
              onChange({ ...draft, modelChanged: true, modelId: event.target.value })
            }
            value={draft.modelId}
          >
            {selection.modelMixed && !draft.modelId ? (
              <option value="">不修改设备型号</option>
            ) : null}
            {selection.models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.displayName}
              </option>
            ))}
          </select>
          <ChevronDown aria-hidden="true" size={15} />
        </div>
      </label>
    </section>
  );
}

function RegionLiftSettings({
  disabled,
  draft,
  onChange
}: {
  disabled: boolean;
  draft: LiftRegionDraft;
  onChange: (draft: LiftRegionDraft) => void;
}) {
  const selection = draft.selection;
  return (
    <section className="cargo-region-object-section">
      <div className="cargo-region-object-header">
        <div>
          <h4>托盘往复式提升机</h4>
          <span>{selection.liftIds.length} 台</span>
        </div>
        {selection.layerCountMixed ? <small>原层数不一致，已显示默认值</small> : null}
      </div>
      <label className="cargo-region-number-field">
        <span>统一层数</span>
        <input
          disabled={disabled}
          min="1"
          onChange={(event) =>
            onChange({ ...draft, layerCount: event.target.value, layerCountChanged: true })
          }
          step="1"
          type="number"
          value={draft.layerCount}
        />
      </label>
    </section>
  );
}

function RegionShuttleSettings({
  disabled,
  draft,
  onChange
}: {
  disabled: boolean;
  draft: ShuttleRegionDraft;
  onChange: (draft: ShuttleRegionDraft) => void;
}) {
  const selection = draft.selection;
  return (
    <section className="cargo-region-object-section">
      <div className="cargo-region-object-header">
        <div>
          <h4>单工位往复式穿梭车</h4>
          <span>{selection.shuttleIds.length} 台</span>
        </div>
        {selection.heightMixed ? <small>原高度不一致，已显示默认值</small> : null}
      </div>
      <label className="cargo-region-number-field">
        <span>统一车身高度 (mm)</span>
        <input
          disabled={disabled}
          min="1"
          onChange={(event) =>
            onChange({ ...draft, height: event.target.value, heightChanged: true })
          }
          step="1"
          type="number"
          value={draft.height}
        />
      </label>
    </section>
  );
}
function RegionStackerCraneSettings({
  disabled,
  draft,
  onChange
}: {
  disabled: boolean;
  draft: StackerCraneRegionDraft;
  onChange: (draft: StackerCraneRegionDraft) => void;
}) {
  const selection = draft.selection;
  return (
    <section className="cargo-region-object-section">
      <div className="cargo-region-object-header">
        <div>
          <h4>托盘堆垛机</h4>
          <span>{selection.stackerCraneIds.length} 台</span>
        </div>
        {selection.heightMixed ? <small>原高度不一致，已显示默认值</small> : null}
      </div>
      <label className="cargo-region-number-field">
        <span>统一立柱高度 (mm)</span>
        <input
          disabled={disabled}
          min="1"
          onChange={(event) =>
            onChange({ ...draft, height: event.target.value, heightChanged: true })
          }
          step="1"
          type="number"
          value={draft.height}
        />
      </label>
    </section>
  );
}

function createRegionDrafts(regions: CargoEditorBatchSettingRegion[]) {
  return Object.fromEntries(
    regions.flatMap((region) =>
      region.selections.map((selection) => [
        createRegionDraftKey(region.id, selection),
        createRegionDraft(selection)
      ])
    )
  ) as Record<string, RegionDraft>;
}

function createRegionDraft(selection: CargoEditorBatchSelection): RegionDraft {
  if (selection.kind === "rack-batch") {
    return {
      cargo: "",
      kind: selection.kind,
      layerRows: createRackLayerRangeRows(selection.layerHeights),
      layerRowsChanged: false,
      selection
    };
  }
  if (selection.kind === "device-batch") {
    return {
      kind: selection.kind,
      modelChanged: false,
      modelId: selection.modelMixed ? "" : selection.modelId,
      selection
    };
  }
  if (selection.kind === "pallet-stacker-crane-batch") {
    return {
      height: String(Math.round(selection.height * 1000)),
      heightChanged: false,
      kind: selection.kind,
      selection
    };
  }
  if (selection.kind === "single-station-reciprocating-shuttle-batch") {
    return {
      height: String(Math.round(selection.height * 1000)),
      heightChanged: false,
      kind: selection.kind,
      selection
    };
  }
  return {
    kind: selection.kind,
    layerCount: String(selection.layerCount),
    layerCountChanged: false,
    selection
  };
}

function createRegionDraftKey(regionId: string, selection: CargoEditorBatchSelection) {
  if (selection.kind === "rack-batch") {
    return `${regionId}:rack:${selection.rackType}`;
  }
  if (selection.kind === "device-batch") {
    return `${regionId}:device:${selection.blockPrefix}`;
  }
  if (selection.kind === "pallet-stacker-crane-batch") {
    return `${regionId}:pallet-stacker-crane`;
  }
  if (selection.kind === "single-station-reciprocating-shuttle-batch") {
    return `${regionId}:single-station-reciprocating-shuttle`;
  }
  return `${regionId}:pallet-reciprocating-lift`;
}

function validateRegionDrafts(drafts: Record<string, RegionDraft>) {
  for (const draft of Object.values(drafts)) {
    if (draft.kind === "rack-batch" && draft.layerRowsChanged) {
      const result = parseRackLayerRangeRows(draft.layerRows);
      if (!result.ok) {
        return `${draft.selection.rackType === "bin" ? "料箱货架" : "横梁式货架"}：${result.error}`;
      }
    }
    if (draft.kind === "device-batch" && draft.modelChanged && !draft.modelId) {
      return `设备“${draft.selection.blockPrefix}”需要选择一个型号`;
    }
    if (draft.kind === "pallet-reciprocating-lift-batch" && draft.layerCountChanged) {
      const layerCount = Number(draft.layerCount);
      if (!Number.isInteger(layerCount) || layerCount <= 0) {
        return "托盘往复式提升机层数必须是大于 0 的整数";
      }
    }
    if (draft.kind === "pallet-stacker-crane-batch" && draft.heightChanged) {
      const height = Number(draft.height);
      if (!Number.isFinite(height) || height <= 0) {
        return "托盘堆垛机立柱高度必须大于 0";
      }
    }
    if (draft.kind === "single-station-reciprocating-shuttle-batch" && draft.heightChanged) {
      const height = Number(draft.height);
      if (!Number.isFinite(height) || height <= 0) {
        return "单工位往复式穿梭车车身高度必须大于 0";
      }
    }
  }
}

function countRegionMembers(region: CargoEditorBatchSettingRegion) {
  return region.selections.reduce((count, selection) => {
    if (selection.kind === "rack-batch") {
      return count + selection.rackIds.length;
    }
    if (selection.kind === "device-batch") {
      return count + selection.deviceIds.length;
    }
    if (selection.kind === "pallet-stacker-crane-batch") {
      return count + selection.stackerCraneIds.length;
    }
    if (selection.kind === "single-station-reciprocating-shuttle-batch") {
      return count + selection.shuttleIds.length;
    }
    return count + selection.liftIds.length;
  }, 0);
}
